import { NewCampaignState } from "@/services/new-campaign.service";
import { createLabeledEnum, InferLabeledEnum } from "@/utils/labeled-enum";
import {
  comparisonOperators,
  comparisonWithBetweenOperators,
  dateOperators,
  dateOperatorsWithBetween,
  labelOperators,
  numericOperators,
  numericOperatorsWithBetween,
  textOperators,
} from "@/utils/operators";
import {
  ClockIcon,
  DownloadIcon,
  LucideIcon,
  MonitorSmartphoneIcon,
  UnplugIcon,
  ZapIcon,
} from "lucide-react";
import { z } from "zod";
import type { FieldName } from "./filters";

export const CLEAR_DATE_STRING = "0001-01-01T00:00:00Z";

const allOperatorsSchema = z.union([
  numericOperators.enum,
  textOperators.enum,
  comparisonOperators.enum,
  comparisonWithBetweenOperators.enum,
  numericOperatorsWithBetween.enum,
  dateOperators.enum,
  dateOperatorsWithBetween.enum,
  labelOperators.enum,
  z.literal("Null"),
  z.literal("NotNull"),
  z.literal("ListDoesNotContain"),
  z.literal("ListContains"),
]);

export type Statistics = {
  grouped: {
    campaignId: number;
    configurationId: number;
    counts: {
      none: number;
      new: number;
      disqualified: number;
      postponed: number;
      inProgress: number;
      closedWon: number;
      closedLost: number;
    };
    period: number;
    year: number;
    periodStart: string;
    periodEnd: string;
  }[];
  all: {
    none: number;
    new: number;
    disqualified: number;
    postponed: number;
    inProgress: number;
    closedWon: number;
    closedLost: number;
  }[];
  totals: {
    none: number;
    new: number;
    disqualified: number;
    postponed: number;
    inProgress: number;
    closedWon: number;
    closedLost: number;
  };
};

const fieldNameSchema = z.string().and(z.custom<FieldName>());

export const queryFilterSchema = z.object({
  fieldName: fieldNameSchema,
  filterName: z.string(),
  fieldNames: z.array(fieldNameSchema).nullable().optional(),
  operator: allOperatorsSchema,
  values: z.array(z.string()),
});

export type QueryFilter = z.infer<typeof queryFilterSchema>;

export const eventQueryFilterSchema = z.object({
  fieldName: fieldNameSchema,
  filterName: z.string(),
  fieldNames: z.array(fieldNameSchema).nullable().optional(),
  operator: z.literal("EventFilter"),
  values: z.array(z.string()).nullable().optional(),
  eventType: z.string(),
  timeOperator: dateOperators.enum.nullable().optional(),
  timeValues: z.array(z.string()).nullable().optional(),
  numberOperator: numericOperators.enum.nullable().optional(),
  numberValues: z.array(z.string()).nullable().optional(),
  stringOperator: textOperators.enum.nullable().optional(),
  stringValues: z.array(z.string()).nullable().optional(),
});

export type EventQueryFilter = z.infer<typeof eventQueryFilterSchema>;

export const departmentSizeQueryFilterSchema = z.object({
  fieldName: fieldNameSchema,
  filterName: z.string(),
  fieldNames: z.array(fieldNameSchema).nullable().optional(),
  operator: z.literal("DepartmentSize"),
  values: z.array(z.string()).nullable().optional(),
  groups: z.array(
    z.object({
      operator: numericOperators.enum,
      values: z.array(z.string()),
    }),
  ),
});

export type DepartmentSizeQueryFilter = z.infer<
  typeof departmentSizeQueryFilterSchema
>;

export const querySchema = z.object({
  limit: z.number().nullable(),
  offset: z.number(),
  filters: z.array(
    z.union([
      queryFilterSchema,
      eventQueryFilterSchema,
      departmentSizeQueryFilterSchema,
    ]),
  ),
  descendingOrder: z.boolean(),
  orderBy: z.string().nullable(),
});

export type Query = z.infer<typeof querySchema>;

export type ContactConfigurationRoleFilter = {
  titleOperator: string | null;
  title?: string | null;
  level: string | null;
  group: string | null;
};

export const personaSchema = z.object({
  groups: z.array(z.string()),
  levels: z.array(z.string()),
  titles: z.array(
    z.object({
      title: z.string(),
      operator: z.enum(["Equal", "Contains"]),
    }),
  ),
  requirePhone: z.boolean(),
});

export type Persona = z.infer<typeof personaSchema>;

export const contactConfigurationSchema = z.object({
  requireContact: z.boolean(),
  personas: z.array(personaSchema).optional(),
});

export type ContactConfiguration = z.infer<typeof contactConfigurationSchema>;

export type ContactConfig = {
  id: string;
  role: { group: string | null; level: string | null };
  title?: string | null;
  titleOperator?: "Equal" | "Contains";
};

export type PersonContact = {
  personas: Persona[];
};

export type ScheduleChange = {
  scheduleId: number;
  date: string;
  amount: number;
};

export const newScheduleChangeSchema = z.object({
  date: z.string(),
  amount: z.number(),
});

export type NewScheduleChange = z.infer<typeof newScheduleChangeSchema>;

export type ScheduleChangeUpdate = {
  date: string;
  amount: number | null;
};

export type Schedule = {
  id: number;
  dayOfWeek: string;
  hourOfDay: number;
  minuteOfHour: number;
  amount: number;
  scheduleChanges: ScheduleChange[];
};

export const newScheduleSchema = z.object({
  id: z.number().nullable().optional(),
  dayOfWeek: z.string(),
  hourOfDay: z.number().nullable().optional(),
  minuteOfHour: z.number().nullable().optional(),
  amount: z.number(),
  scheduleChanges: z.array(newScheduleChangeSchema).nullable().optional(),
});

export type NewSchedule = z.infer<typeof newScheduleSchema>;

export type ScheduleUpdate = Partial<
  Omit<Schedule, "scheduleChanges"> & {
    scheduleChanges: ScheduleChangeUpdate[];
  }
> &
  Pick<Schedule, "dayOfWeek">;

export type SalesStrat = {
  id: string;
  label: string;
  description: string;
  image: string;
  configurations: {
    gold: {
      idealLeadShare: number;
      priority: number;
      subpriority: number;
    };
    silver: {
      idealLeadShare: number;
      priority: number;
      subpriority: number;
    };
    bronze: {
      idealLeadShare: number;
      priority: number;
      subpriority: number;
    };
  };
};

// TODO: maybe we need a separate type for received configurations vs
// configurations that we send to the backend?
export const configurationSchema = z.object({
  id: z.number().nullable().optional(),
  enabled: z.boolean().nullable().optional(),
  deleted: z.boolean().nullable().optional(),
  campaignId: z.number().nullable().optional(),
  name: z.string().nullable().optional(),
  query: querySchema,
  contactConfiguration: contactConfigurationSchema.nullable(),
  priority: z.number(),
  subPriority: z.number().nullable().optional(),
  idealLeadShare: z.number(),
  quality: z.number(),
  estimatedRemaining: z.number().nullable(),
});

export type Configuration = z.infer<typeof configurationSchema>;

export const campaignTypes = ["OnDemand", "Scheduled", "Triggered"] as const;

export type CampaignType = (typeof campaignTypes)[number] | (string & {});

export const campaignStatuses = [
  "Inactive",
  "Active",
  "Ended",
  "Archived",
] as const;

export type CampaignStatus = (typeof campaignStatuses)[number] | (string & {});

export const campaignStates = ["Normal", "NearlyDepleted", "Depleted"] as const;

export type CampaignState = (typeof campaignStates)[number] | (string & {});

export type NewCampaign = Pick<
  Campaign,
  "type" | "name" | "status" | "configurations" | "maxLeadsPerWeek" | "triggers"
> & {
  schedules: NewSchedule[];
} & Partial<Omit<Campaign, "schedules">>;

export const stopListSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  description: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  mandatory: z.boolean().nullable().optional(),
  lassoid: z.string().nullable().optional(),
  campaignList: z.boolean().nullable().optional(),
  count: z.number(),
  hidden: z.boolean(),
  campaignCount: z.number(),
  uniqueId: z.string(),
  virtual: z.boolean().nullable().optional(),
});

export type StopList = z.infer<typeof stopListSchema>;

export type Campaign = {
  id: number;
  name: string;
  type: CampaignType;
  lassoOrgId: string;
  responsibleId: string | null;
  creatorId: string | null;
  completionEmail: string | null;
  exportSystem: string | null;
  exportMode: "SalesAutomatic" | "SalesManual" | null;
  exportInput: Record<string, string> | null;
  created: string;
  status: CampaignStatus;
  statusTimestamp: string;
  state: CampaignState;
  stateTimestamp: string;
  chosenStartDate: string | null;
  chosenEndDate: string | null;
  configurations: Configuration[];
  schedules: Schedule[];
  // Included if includeStatistics is true
  statistics?: Partial<Record<LeadStatus | "total", number>>;
  triggers: Array<{ event: string }>;
  estimatedEndDate: string | null;
  estimatedRemaining: number | null;
  maxLeadsPerWeek: number | null;
  stopLists: StopList[] | null;
  deliverContacts: boolean;
  deliverContactsRequirePhone: boolean;
  deliverLiveNumber: boolean;
};

export type CampaignUpdate = Partial<
  Omit<Campaign, "schedules"> & {
    schedules: ScheduleUpdate[];
    removeIntegration: boolean;
  }
>;

export type SimpleCampaign = {
  id: number;
  creatorId: string | null;
  responsibleId: string | null;
  responsibleName: string | null;
  name: string;
  type: CampaignType;
  status: CampaignStatus;
  state: CampaignState;
  configurations: Configuration[];
};

export const leadStatuses = [
  "New",
  "Postponed",
  "ClosedWon",
  "ClosedLost",
  "Disqualified",
  "InProgress",
  "Scheduled",
  "FollowUpDue",
  "Rejected",
  "Qualified",
  "None",
] as const;

export const leadDeliveryStatuses = [
  "Pending", // Lead is pending delivery (to a third party system), and may yet fail.
  "Delivered", // These are internal statuses not related to the lead-flow itself, but to the delivery of them.
  "IntegrationFailed", // Integration with third party system failed, but it is still delivered to Lasso and costs credits.
  "Skipped", // Skipped due to data not being valid anymore
  "SkippedContactMissing", // Skipped due to contact missing
  "Duplicate", // Duplicate in third party system
  "InsufficientCredits", // Not enough credits to deliver the lead
] as const;

export enum LeadDeliveryStatusEnum {
  Pending = "Pending", // Lead is pending delivery (to a third party system), and may yet fail.
  Delivered = "Delivered", // These are internal statuses not related to the lead-flow itself, but to the delivery of them.
  IntegrationFailed = "IntegrationFailed", // Integration with third party system failed, but it is still delivered to Lasso and costs credits.
  Skipped = "Skipped", // Skipped due to data not being valid anymore
  SkippedContactMissing = "SkippedContactMissing", // Skipped due to contact missing
  Duplicate = "Duplicate", // Duplicate in third party system
  InsufficientCredits = "InsufficientCredits", // Not enough credits to deliver the lead
}

export type LeadDeliveryStatus = (typeof leadDeliveryStatuses)[number];

export const labeledLeadDeliveryStatuses = createLabeledEnum({
  Pending: "Afventer levering",
  Delivered: "Leveret",
  IntegrationFailed: "Integration mislykkedes",
  Skipped: "Springet over",
  SkippedContactMissing: "Springet over - kontakt mangler",
  Duplicate: "Duplikat",
  InsufficientCredits: "Utilstrækkelige credits",
});

export type LeadDeliveryStatusLabel = InferLabeledEnum<
  typeof labeledLeadDeliveryStatuses
>;

export type LeadStatus = (typeof leadStatuses)[number];

export type LeadResponse = {
  history: LeadHistory[];
  lead: Lead;
  thirdPartyLink: string | null;
};

export type LeadHistory = {
  id: number;
  timestamp: string;
  status: LeadStatus;
  statusReason: string;
  followUpTime: string | null;
  changedById: string;
};

export type Lead = {
  id: number;
  campaignId: number;
  configurationId: number;
  runId: number;
  lassoId: string;
  thirdPartyId: string | null;
  events: string[] | null; // TODO: type with proper sales trigger events instead of just strings
  name: string;
  updated: string | null;
  created: string;
  feedback: LeadFeedback | null;
  status: LeadStatus;
  creditsUsed: number | null;

  deliveryStatus: LeadDeliveryStatus | null;
  deliveryError: string | null;

  creditsUsedOnContact: number | null;
  contactPurchaseDate: string | null;
  contactPurchased: boolean;
  contactLoadTime: string | null;

  followUpTime: string | null;
  statusReason: string | null;
  contact: Contact | null;
};

export type LeadDue = {
  history: LeadHistory[];
  lead: Lead;
};

// ? This is technically a different (but basically identical object in backend, however, for less duplicated code, I reused the existing type)
export interface Contact extends ContactResponse {
  isAnonymous: boolean;
}

export enum LeadFeedback {
  BadPhonenumber = "BadPhonenumber",
  WrongTargetGroup = "WrongTargetGroup",
  InvalidContact = "InvalidContact",
  ContactNoLongerExists = "ContactNoLongerExists",
  Other = "Other",
}

export type ReportLeadErrorDto = {
  feedback: LeadFeedback;
  lassoId: string;
  context?: string;
  disqualified: boolean;
  refund: boolean;
  comment?: string;
};

export type LeadsResponse = {
  results: Lead[];
  page: number;
  pageSize: number;
  totalPages: number;
  resultsFound: number;
  resultsReturned: number;
};

export type LeadsDueResponse = {
  results: LeadDue[];
  page: number;
  pageSize: number;
  totalPages: number;
  resultsFound: number;
  resultsReturned: number;
};

export type CountsResponse = {
  configId: number;
  count: number;
  quality: number;
  hasContacts: boolean;
  priority: number;
  subPriority: number;
};

export type ConfigurationSuggestion = {
  configuration: Configuration;
  count: number;
};

export type ContactResponse = {
  id: string | null;
  uniqueId: string | null;
  name: string | null;
  titles: Array<string>;
  type: string | null;
  group: string | null;
  emails: Array<string>;
  phoneNumbers: Array<string>;
  possiblePhoneNumbers: Array<string>;
  links: Array<string>;
  paths: Array<string>;
  allGroups: Array<string>;
  obfuscated: boolean;
};
export function createContactResponse(
  options: Partial<ContactResponse>,
): ContactResponse {
  return {
    id: null,
    uniqueId: null,
    name: null,
    titles: [],
    type: null,
    group: null,
    emails: [],
    phoneNumbers: [],
    possiblePhoneNumbers: [],
    links: [],
    paths: [],
    allGroups: [],
    obfuscated: false,
    ...options,
  };
}

export type ContactsFormValues = {
  contacts: {
    role: {
      group: string | null;
      level: string | null;
    };
    title?: string | null;
  }[];
  personas?: Persona[];
  requirePhone: boolean;
};

export const createCampaignSearchSchema = z.object({
  type: z.enum(["standard", "trigger"]),
});

export type TOverviewPeriod = {
  campaignId: number;
  count: number;
  creditsAdHoc: number;
  creditsAuto: number;
  creditsUsed: number;
  liveNumberAuto: number;
  liveNumberAdHoc: number;
  contactsAuto: number;
  contactsAdHoc: number;
  refunded: number;
};

export type TOverviewCounts = {
  counts: TOverviewPeriod[];
  periodEnd: string;
  periodStart: string;
};

export type TOverviewUsageResponse = {
  allTime: {
    ClosedLost: number;
    ClosedWon: number;
    Disqualified: number;
    FollowUpDue: number;
    InProgress: number;
    New: number;
    Postponed: number;
    Qualified: number;
    Refunded: number;
    Rejected: number;
    total: number;
  };
  periods: TOverviewCounts[];
};

export type SuggestionsRequest = {
  count: number;
  configuration: Configuration;
  stopListIds: string[];
  doNotModify: string[];
  campaignId?: number;
  leadsPerWeek?: number;
  minimumRuntimeInWeeks?: number;
};

export type ConfigurationRequest = {
  configurations: Configuration[];
  stopListIds: string[];
  /** Excludes leads this campaign has already delivered from the counts. */
  campaignId?: number;
};

export const deliveryTypes = [
  {
    type: "LassoSales",
    icon: MonitorSmartphoneIcon,
    name: "Lasso Sales",
    description: "Arbejd med leads direkte i Lasso Sales.",
  },
  {
    type: "Excel",
    icon: DownloadIcon,
    name: "Excel",
    description: "Få leads leveret som en Excel-fil til din email.",
  },
  {
    type: "Integration",
    icon: UnplugIcon,
    name: "Integration",
    description: "Send leads direkte til dit CRM-system eller dialer.",
  },
] satisfies {
  type: NonNullable<NewCampaignState["delivery"]>["type"];
  icon: LucideIcon;
  name: string;
  description: string;
}[];

export const planningTypes = [
  {
    type: "OnDemand",
    icon: DownloadIcon,
    name: "On-demand",
    description: "Modtag leads med det samme, og herefter når du ønsker det.",
  },
  {
    type: "Scheduled",
    icon: ClockIcon,
    name: "Flow",
    description:
      "Modtag leads i et fast interval, f.eks. hver mandag og onsdag.",
  },
  {
    type: "Triggered",
    icon: ZapIcon,
    name: "Trigger",
    isPremium: true,
    description:
      "Modtag leads ved hændelser, såsom stiftelse eller adresseskift",
  },
] satisfies {
  type: CampaignType;
  icon: LucideIcon;
  name: string;
  description: string;
  isPremium?: boolean;
}[];
